import { Component, Input, OnInit, Output, EventEmitter, ViewChildren, ElementRef, QueryList, ChangeDetectionStrategy } from "@angular/core";

import { BaseComponent, UserService, ExchangeService, environment, HelperService, CommunityService, ContentService, ProductService, AdminService, encodeFilenameForHeader } from "@mypxplat/xplat/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { AppService, WebCommunityService } from "@mypxplat/xplat/web/core";
import { DatePickerComponent } from "../date-picker/date-picker.component";
import { Subject, interval, skip, takeUntil, throttle } from "rxjs";
import { FileUploader } from "ng2-file-upload";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-add-edit-announcement",
  templateUrl: "add-edit-announcement.component.html",
  standalone: false,
})
export class AddEditAnnouncementComponent extends BaseComponent implements OnInit {
  @Input() announcement: any;
  @Output() announcementAdded: EventEmitter<any> = new EventEmitter();
  public loading: boolean;
  public previewDescription: string = "";
  private _preview: boolean = false;
  set preview(value: boolean) {
    this._preview = value;
    if (value && this.announcement?.enDescription) {
      this.previewDescription = this.helperService.sanitizeQuillHtml(this.announcement.enDescription);
    }
  }
  get preview() {
    return this._preview;
  }
  public audience: any = {
    web: true,
    mobile: false,
  };

  public date: Date;
  public dateDisplay: string = "";
  public endDate: Date;
  public endDateDisplay: string = "";
  public dateTimezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;
  public timezoneMode: "local" | "fixed" = "fixed";

  // Content type: visual editor, raw HTML, or image
  public contentType: "visual" | "html" | "image" = "visual";

  // Image upload
  public imageUploader: FileUploader;
  public imageUrl: string;
  public imageLink: string = "";
  public displayImage: string | ArrayBuffer;
  public imageUploadProgress: number;

  // Product filter
  public productFilterMode: "include" | "exclude" = "include";
  public productResults: Array<any>;
  private _productQuery: string;
  private _productQueryStream$: Subject<any> = new Subject();
  set productQuery(query) {
    this._productQuery = query;
    if (this._productQuery) {
      this._productQueryStream$.next(query);
    } else {
      this.productResults = undefined;
    }
  }

  get productQuery() {
    return this._productQuery;
  }
  public selectedProductStringIds: Array<any> = [];

  // Subscription filter
  public subscriptionFilter: "all" | "members_only" | "non_members_only" = "all";

  // Country targeting
  public selectedCountries: string[] = [];
  public countryFilterMode: "include" | "exclude" = "include";
  public countryList: any[] = [];
  public countryNameMap: { [code: string]: string } = {};
  public filteredCountryList: any[] = [];
  public countryQuery: string = "";

  public groups: Array<any>;
  public groupMap: any = {};
  public announcementGroups: Array<any> = [];
  public userGroupsCollapsed: boolean = true;
  public loadingGroups: boolean = false;
  constructor(
    userService: UserService,
    public activeModal: NgbActiveModal,
    public exchangeService: ExchangeService,
    public helperService: HelperService,
    public appService: AppService,
    public communityService: WebCommunityService,
    public contentService: ContentService,
    public productService: ProductService,
    public adminService: AdminService
  ) {
    super(userService);
  }

  ngOnInit() {
    if (!this.announcement) {
      this.announcement = {};
    } else {
      this.announcement.enDescription = this.announcement.description?.en || "";
      if (this.announcement.audience) {
        this.audience.web = this.announcement.audience.includes("web");
        this.audience.mobile = this.announcement.audience.includes("mobile");
      }
      this.date = this.announcement.date ? this.announcement.date.toDate() : new Date();
      if (this.announcement.products) this.selectedProductStringIds = [...this.announcement.products];
      if (this.announcement.group_ids) this.userGroupsCollapsed = false;
      // Product filter mode
      this.productFilterMode = this.announcement.product_filter_mode || "include";
      // Subscription filter (backward compat with members_only boolean)
      if (this.announcement.subscription_filter) {
        this.subscriptionFilter = this.announcement.subscription_filter;
      } else if (this.announcement.members_only) {
        this.subscriptionFilter = "members_only";
      }
      // Country targeting
      if (this.announcement.countries) this.selectedCountries = [...this.announcement.countries];
      if (this.announcement.country_filter_mode) this.countryFilterMode = this.announcement.country_filter_mode;
      // Timezone mode
      if (this.announcement.timezone_mode) this.timezoneMode = this.announcement.timezone_mode;
      if (this.announcement.timezone) this.dateTimezone = this.announcement.timezone;
      // End date
      if (this.announcement.end_date) {
        this.endDate = this.announcement.end_date.toDate();
        this.updateEndDateDisplay();
      }
      // Content type
      if (this.announcement.image_url) {
        this.contentType = "image";
        this.imageUrl = this.announcement.image_url;
        this.imageLink = this.announcement.image_link || "";
      }
      this.updateDateDisplay();
    }

    this.setupImageUploader();

    // Load groups
    this.loadingGroups = true;
    this.adminService.get("groups").subscribe((result: any) => {
      this.groups = result;
      this.loadingGroups = false;
      this.groups.forEach((group) => {
        group.selected = this.announcement.group_ids && this.announcement.group_ids.includes(group.name);
        this.groupMap[group.id] = group;
      });
    });

    // Load countries
    this.userService.listCountries().subscribe((result: any) => {
      this.countryList = [];
      for (const code in result) {
        this.countryList.push({ code, name: result[code] });
        this.countryNameMap[code] = result[code];
      }
      this.countryList.sort((a, b) => a.name.localeCompare(b.name));
    });

    this._productQueryStream$
      .pipe(
        skip(1),
        throttle(
          () => {
            return interval(1000);
          },
          { leading: true, trailing: true }
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((result) => {
        this.productAutocomplete();
      });
  }

  setupImageUploader() {
    this.imageUploader = new FileUploader({
      isHTML5: true,
      method: "PUT",
      queueLimit: 1,
      autoUpload: false,
      disableMultipart: true,
      url: "",
    });
    this.imageUploader.onAfterAddingFile = (file) => {
      file.withCredentials = false;
      if (file.file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = () => {
          this.displayImage = reader.result;
        };
        reader.readAsDataURL(file._file);
        this.uploadImage();
      } else {
        alert("Please select an image file");
        this.imageUploader.clearQueue();
      }
    };
    this.imageUploader.onProgressAll = (progress: any) => {
      this.imageUploadProgress = progress;
    };
    this.imageUploader.onCompleteAll = () => {
      this.imageUploadProgress = null;
    };
  }

  uploadImage() {
    const file = this.imageUploader.queue[0];
    this.imageUploadProgress = 1;
    this.contentService
      .getPresignedUploadURL({
        folder: `announcements/${this.announcement.string_id || "temp"}`,
        fileName: file.file.name,
        type: "generic_file",
      })
      .subscribe((result: any) => {
        if (result.file) {
          this.imageUrl = result.file.url;
          let uploadHeaders = [];
          uploadHeaders.push({ name: "Content-Type", value: undefined });
          uploadHeaders.push({ name: "Content-Disposition", value: encodeFilenameForHeader(file.file.name) });
          file.headers = uploadHeaders;
          file.url = result.url;
          this.imageUploader.uploadItem(file);
        } else {
          this.imageUploadProgress = null;
          alert("There was an error uploading this file, please try again.");
        }
      });
  }

  removeImage() {
    this.displayImage = null;
    this.imageUrl = null;
    this.imageUploader.clearQueue();
  }

  filterCountries() {
    if (this.countryQuery) {
      this.filteredCountryList = this.countryList.filter((c) => c.name.toLowerCase().includes(this.countryQuery.toLowerCase()));
    } else {
      this.filteredCountryList = [];
    }
  }

  selectCountry(country) {
    if (!this.selectedCountries.includes(country.code)) {
      this.selectedCountries.push(country.code);
    }
    this.countryQuery = "";
    this.filteredCountryList = [];
  }

  removeCountry(code) {
    this.selectedCountries = this.selectedCountries.filter((c) => c !== code);
  }

  public searchingProducts: boolean = false;

  productAutocomplete() {
    if (this.productQuery) {
      this.searchingProducts = true;
      this.productService.searchv2("title", this.productQuery).subscribe((result: Array<any>) => {
        this.searchingProducts = false;
        this.productResults = result;
      });
    } else {
      this.searchingProducts = false;
      this.productResults = [];
    }
  }

  removeProduct(stringId) {
    this.selectedProductStringIds = this.selectedProductStringIds.filter((id) => id != stringId);
  }

  selectProduct(product) {
    this.selectedProductStringIds.push(product.stringId);
  }

  showDatePicker() {
    let modalInstance = this.appService.showModal(DatePickerComponent, { size: "lg", keyboard: true, ariaLabelledBy: "modal-title" });
    if (this.announcement.id || this.date) modalInstance.componentInstance.initWithDate = this.date;
    modalInstance.componentInstance.past = false;
    modalInstance.componentInstance.includeTimePicker = true;
    modalInstance.componentInstance.includeTimeZonePicker = true;
    if (this.dateTimezone) modalInstance.componentInstance.selectedTimezone = this.dateTimezone;
    modalInstance.componentInstance.dateSelected.subscribe((result) => {
      this.date = result.date;
      this.dateTimezone = result.timezone;
      this.updateDateDisplay();
    });
  }

  updateDateDisplay() {
    if (!this.date) {
      this.dateDisplay = "";
      return;
    }
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const m = months[this.date.getUTCMonth()];
    const d = this.date.getUTCDate();
    const y = this.date.getUTCFullYear();
    let h = this.date.getUTCHours();
    const min = this.date.getUTCMinutes().toString().padStart(2, "0");
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    this.dateDisplay = `${m} ${d}, ${y} ${h}:${min} ${ampm}`;
  }

  showEndDatePicker() {
    let modalInstance = this.appService.showModal(DatePickerComponent, { size: "lg", keyboard: true, ariaLabelledBy: "modal-title" });
    if (this.endDate) modalInstance.componentInstance.initWithDate = this.endDate;
    modalInstance.componentInstance.past = false;
    modalInstance.componentInstance.includeTimePicker = true;
    modalInstance.componentInstance.includeTimeZonePicker = true;
    if (this.dateTimezone) modalInstance.componentInstance.selectedTimezone = this.dateTimezone;
    modalInstance.componentInstance.dateSelected.subscribe((result) => {
      this.endDate = result.date;
      this.updateEndDateDisplay();
    });
  }

  updateEndDateDisplay() {
    if (!this.endDate) {
      this.endDateDisplay = "";
      return;
    }
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const m = months[this.endDate.getUTCMonth()];
    const d = this.endDate.getUTCDate();
    const y = this.endDate.getUTCFullYear();
    let h = this.endDate.getUTCHours();
    const min = this.endDate.getUTCMinutes().toString().padStart(2, "0");
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    this.endDateDisplay = `${m} ${d}, ${y} ${h}:${min} ${ampm}`;
  }

  removeEndDate() {
    this.endDate = null;
    this.endDateDisplay = "";
  }

  submit() {
    const hasContent = this.contentType === "image" ? !!this.imageUrl : !!this.announcement.enDescription;
    if (this.announcement.show_for_new_signups && !this.endDate) {
      alert("An end date is required when 'Show for new signups' is enabled.");
      return;
    }
    if (this.announcement.string_id && this.announcement.title && hasContent && (this.audience.web || this.audience.mobile)) {
      this.appService
        .confirm(`Are you sure you want to add this announcement? If you click Confirm, it will show to users based on your selected filters on your selected start date.`)
        .then((result) => {
          if (!result) return;
          let args: any = {
            string_id: this.announcement.string_id,
            title: this.announcement.title,
            date: this.date,
            dev: this.announcement.dev || false,
            show_for_new_signups: this.announcement.show_for_new_signups || false,
          };
          // Content
          if (this.contentType === "image") {
            args.image_url = this.imageUrl;
            args.image_link = this.imageLink || null;
            args.description = { en: "" };
          } else {
            args.description = { en: this.announcement.enDescription };
            args.image_url = null;
            args.image_link = null;
          }
          // Groups
          if (!this.userGroupsCollapsed) {
            let groupIds = [];
            for (const i in this.groupMap) {
              if (this.groupMap[i].selected) groupIds.push(this.groupMap[i].name);
            }
            args.group_ids = groupIds;
          }
          // Products
          if (this.selectedProductStringIds && this.selectedProductStringIds.length) {
            args.products = this.selectedProductStringIds;
            args.product_filter_mode = this.productFilterMode;
          }
          // Audience
          let audience = [];
          if (this.audience.web) audience.push("web");
          if (this.audience.mobile) audience.push("mobile");
          args.audience = audience;
          // Subscription filter
          args.subscription_filter = this.subscriptionFilter;
          // Timezone
          args.timezone = this.dateTimezone;
          args.timezone_mode = this.timezoneMode;
          // End date
          args.end_date = this.endDate || null;
          // Country targeting
          if (this.selectedCountries.length) {
            args.countries = this.selectedCountries;
            args.country_filter_mode = this.countryFilterMode;
          }
          this.communityService.addAnnouncement(this.announcement.id || this.announcement.string_id, args).then((result) => {
            this.announcementAdded.next(true);
            this.activeModal.close();
          });
        });
    } else {
      alert("Please fill out all the required fields.");
    }
  }
}
